import { themeVar as colors, type as typeTokens } from "../design-tokens";
import { blockStyle } from "../ui/Block";

/**
 * WHO THE BRIEF IS ADDRESSED TO — ASKED, NEVER GUESSED (2026-08-05, owner decision).
 *
 * "its starting with a name, and the name is wrong! where is it pulling the name from for the
 * property owner? maybe we take the name out of it. if the realtor wants to add a name they can."
 *
 * The second half of that sentence is this box. The plumbing on the other end has existed the whole
 * time and has never once been able to fire on this route: `meta.prepared_for` ->
 * `server.amb_build._owner_first_name` -> the narrative packet's `owner_name`, and the "Prepared
 * for" row of the rendered brief. The /next client simply never sent the field, so the ONLY way a
 * name could reach a shipped report was for the writer to invent one. Now there is a legitimate
 * source, and it is a person typing it.
 *
 * THREE THINGS THIS DELIBERATELY DOES NOT DO:
 *
 *   - It does not become required. Blank is the normal answer and the overwhelmingly common one.
 *     When it is blank the key is not sent at all (see `CmaAmbFlow`'s submit `meta`), the server
 *     keeps its "Property owner" default, `owner_name` stays absent from the packet, and the brief
 *     is byte-identical to one built before this box existed.
 *
 *   - It does not validate. `_owner_first_name` already rejects an address, an email, anything with
 *     a digit in it, and a bare honorific, and it takes the first name only. Re-implementing that
 *     rule here would create a second copy of it to drift out of step with the first, and the
 *     client's copy would be the one nobody remembers to update. Whitespace is trimmed on the way
 *     out and nothing else is touched.
 *
 *     WHAT REPLACES VALIDATION IS GUIDANCE (2026-08-05, review). No validation means several
 *     ordinary ways of writing a real name do something other than what the person typing them
 *     expects, and do it in silence. The one that matters is "Alvarez, Susan": `_owner_first_name`
 *     splits on whitespace and takes the first token, so a surname-first name would have the brief
 *     calling the homeowner Alvarez. The fix is the one line of guidance in the label, not a second
 *     copy of the server's rule — the label says which way round to type it, which is the whole of
 *     what a person needs to know and none of what would drift.
 *
 *   - It does not promise a sentence. What the writer does with a supplied name is the narrative
 *     spec's business, and that spec's default is now a lead that opens on the RECOMMENDATION with
 *     no vocative at all — the very defect that created this box. Telling an agent the name is
 *     "used to address the letter" would promise exactly the thing the spec spends a page
 *     forbidding. What this screen can state without guessing is the mechanical part: the name
 *     prints on the report's "Prepared for" line, and leaving it blank means no name is used
 *     anywhere.
 */
export function HomeownerName({
  value,
  onChange,
  ownerOfRecord,
  useOwnerOfRecord,
  onUseOwnerOfRecord,
}: {
  value: string;
  onChange: (next: string) => void;
  /** The owner of record as the CAD sheet prints it, and the sheet's name (2026-09-25). SHOWN, NEVER
   * APPLIED: county records print names surname-first and truncate them ("WALLACE MACKENZIE
   * SIMPSON &"), and the brief greets the first word it is given -- one click would have this brief
   * say "Wallace". The agent reads it and types the name the way the brief should use it. */
  ownerOfRecord?: { name: string; source: string } | null;
  /** Whether the agent confirmed the owner of record for the "Prepared for" line (owner decision 7,
   * 2026-09-25). Unticked by default: shown, not applied. It fills only that line -- never the
   * letter's greeting -- and only when no name is typed above. */
  useOwnerOfRecord?: boolean;
  onUseOwnerOfRecord?: (next: boolean) => void;
}) {
  return (
    <section style={{ marginTop: 22, background: colors.bgSurface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: "14px 16px", fontFamily: typeTokens.fontUi, color: colors.textPrimary }}>
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Who the brief is prepared for</h3>
      <label
        htmlFor="homeowner-name"
        style={{ display: "block", marginTop: 8, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5, ...blockStyle("copy") }}
      >
        Homeowner&apos;s name (optional). It prints on the report&apos;s &ldquo;Prepared for&rdquo;
        line. Type it first name first, the way you would greet them. Leave it blank and the report
        will not use a name.
      </label>
      <input
        id="homeowner-name"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={80}
        autoComplete="off"
        placeholder="First name last name"
        style={{
          marginTop: 8,
          width: "100%",
          maxWidth: 360,
          padding: "8px 12px",
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          fontFamily: typeTokens.fontUi,
          fontSize: 13,
          boxSizing: "border-box",
        }}
      />
      {ownerOfRecord ? (
        <p data-testid="owner-of-record" style={{ margin: "8px 0 0", fontSize: 12, lineHeight: 1.5, color: colors.textSecondary, ...blockStyle("copy") }}>
          The {ownerOfRecord.source} names the owner of record as{" "}
          <strong style={{ color: colors.textPrimary, fontWeight: 600 }}>{ownerOfRecord.name}</strong>.
          County records print names surname first, so type the name the way the brief should greet them.
        </p>
      ) : null}
      {ownerOfRecord && onUseOwnerOfRecord ? (
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 8, fontSize: 12, lineHeight: 1.5, color: colors.textSecondary, ...blockStyle("copy") }}>
          <input
            type="checkbox"
            checked={!!useOwnerOfRecord}
            onChange={(e) => onUseOwnerOfRecord(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            Print the owner of record on the &ldquo;Prepared for&rdquo; line when no name is typed above. It is
            never used to greet anyone in the letter. Leave both empty and the report leaves the line out.
          </span>
        </label>
      ) : null}
    </section>
  );
}
